import React, { useEffect, useRef, useState } from 'react';
import './report.scss';

import { useDispatch, useSelector, useStore } from 'react-redux';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useSearchParams } from 'react-router';
import fileDownload from 'js-file-download';
import {
  getFilterData,
  getPaginationOpts, getResponseError,
  responseLogWithNotification,
  useUpdatePeriod,
} from '../../../utils/utils';
import { deleteReport, loadReportById, loadReportList } from '../actions';
import { getSystemData } from '../../System/reducer';
import { getReportData } from '../reducer';
import Pagination from '../../../components/base/Pagination/Pagination';
import { Button, Icons } from '../../../components/base/BootstrapComponents/BootstrapComponents';
import FilterRow from '../../../components/base/FilterRow/FilterRow';
import FieldDTO from '../../../components/fields/Abstract/FieldDTO';
import { statusFormatter, dateTimeFormatter, userPositionFormatter } from '../../../utils/formatters';
import CustomizedGrid from '../../../components/base/Grid/CustomizedGrid';
import TooltipWithPortal from '../../../components/base/Tooltip/TooltipWithPortal';
import { errorNotification, successNotification } from '../../Notifications/notificationDecorator';
import ReportForm from './ReportForm';
import { loadFileByUrl, showGlobalConfirm } from '../../System/actions';
import getDefaultDataRange from '../../../utils/getDefaultDataRange';
import RangeDatePicker from '../../../components/fields/RangeDatePicker/RangeDatePicker';
import { REPORT_MODULE } from '../../../configs/moduleNames';
import { REPORT_STATUS_NAMES } from '../../../configs';
import ViewFile from '../../System/components/ViewFile';
import getDefaultSort from '../../../utils/getDefaultSort';
import TimerLoader from '../../../components/base/Loader/TimerLoader';

export default function ReportPage() {
  const blockRef = useRef(null);
  const { formatMessage } = useIntl();
  const dispatch = useDispatch();
  const store = useStore();
  const { updatePeriod } = useSelector(getSystemData);
  const {
    reportListMeta,
    loadingReportList,
    reportList,
    loadingCurrentReport,
    currentReport,
    loadingEditReport,
    loadingDeleteReport,
    loadingCreateReport,
  } = useSelector(getReportData);
  const [isShowForm, setIsShowForm] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [showFile, setShowFile] = useState(null);

  const filterName = 'report-filter';

  const [searchParams, setSearchParams] = useSearchParams();
  const isNew = searchParams.get('new');

  const loadData = opts => {
    const paginationOpts = getPaginationOpts(store.getState()[REPORT_MODULE].reportListMeta, opts); // костыль внедрен из за потери контекста в updatePeriod. Он сохранял первую функцию
    const requestData = {
      ...paginationOpts,
      filter: getValues(),
      sort,
    };
    const p1 = dispatch(loadReportList(requestData)).catch(responseLogWithNotification);
    return Promise.all([p1]);
  };

  const rowClassFormatter = item => {
    const classes = ['clickable-table-row'];
    return classes.join(' ');
  };

  const defaultDate = getDefaultDataRange();
  const defaultFilterValues = {
    created_at_from: defaultDate[0], created_at_to: defaultDate[1], search: '', created_at: '',
  };
  const {
    getValues, control, setValue, watch, reset,
  } = useForm({
    defaultValues: defaultFilterValues,
    resetOptions: {
      keepValues: false,
      keepDirtyValues: false,
      keepDefaultValues: false,
    },
  });

  const [setPeriod] = useUpdatePeriod(loadData, updatePeriod);
  useEffect(() => setPeriod(updatePeriod), [updatePeriod]);
  useEffect(() => setIsShowForm(!!isNew), [isNew]);
  useEffect(() => reset(getFilterData(filterName, defaultFilterValues)), []);
  useEffect(() => {
    const subscription = watch(values => {
      loadData({ page: 1 });
      localStorage.setItem(filterName, JSON.stringify(values));
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  const handleEdit = item => event => {
    if (!item) {
      return;
    }
    dispatch(loadReportById(item.id))
      .then(() => {
        setIsShowForm(true);
        setIsEdit(true);
      })
      .catch(response => errorNotification('Загрузка записи', getResponseError(response)));
    return false;
  };

  const handleDownload = item => event => {
    if (!item) {
      return;
    }
    dispatch(loadFileByUrl(item?.files[0]?.url))
      .then(data => {
        if (data) {
          fileDownload(data, item?.files[0]?.file_name);
          successNotification('Скачивание файла', 'Успешно');
        }
      })
      .catch(responseLogWithNotification);
    return false;
  };

  const handleView = item => {
    if (!item) {
      return;
    }

    setShowFile(item?.files);
    return false;
  };

  const handleCloseViewFile = () => {
    setShowFile(null);
  };

  const handleDelete = item => event => {
    dispatch(showGlobalConfirm({
      confirmMessage: `Удаление отчета "${item.name || ''}"`,
      confirmButtonText: 'Удалить',
      confirmClassName: 'remove-preset',
    })).then(() => {
      dispatch(deleteReport(item.id)).then(() => {
        loadData();
        successNotification('Удаление отчета', 'Удаление прошло успешно');
      }).catch(response => {
        errorNotification('Удаление отчета', getResponseError(response));
      });
    }).catch(() => {});
    return false;
  };

  const handleClose = () => {
    setIsShowForm(false);
    setIsEdit(false);
    setSearchParams({});
  };

  const columns = [
    {
      dataField: 'name',
      text: 'Наименование отчета',
    },
    {
      dataField: 'from_date',
      text: 'Дата начала периода',
      formatter: dateTimeFormatter,
    },
    {
      dataField: 'to_date',
      text: 'Дата окончания периода',
      formatter: dateTimeFormatter,
    },
    {
      dataField: 'created_at',
      text: 'Дата создания',
      formatter: dateTimeFormatter,
    },
    {
      dataField: 'status',
      text: 'Статус',
      formatter: a => statusFormatter(a, 'report.status', REPORT_STATUS_NAMES),
    },
    {
      dataField: 'creator',
      text: 'Автор',
      headerClassName: 'user-column',
      formatter: userPositionFormatter,
    },
  ];

  const [sort, setSort] = useState(getDefaultSort(columns));

  const changeSort = fieldName => {
    if (sort && (sort === fieldName || sort === `-${fieldName}`)) {
      if (sort === `-${fieldName}`) {
        setSort(fieldName);
      } else if (sort === fieldName) {
        setSort('');
      }
    } else {
      setSort(`-${fieldName}`);
    }
  };

  useEffect(() => { loadData(); }, [sort]);

  const renderContextMenu = item => (
    <div className="more-wrapper">
      <Button disabled={item.status !== 3} variant="link" className="w-100 more-button" onClick={() => handleView(item)}>
        Просмотреть
      </Button>
      <Button disabled={item.status !== 3} variant="link" className="w-100 more-button" onClick={handleDownload(item)}>
        Скачать
      </Button>
      {/* <Button variant="link" className="w-100 more-button" onClick={handleEdit(item)}> */}
      {/*  Редактировать */}
      {/* </Button> */}
      <Button variant="link" className="w-100 more-button" onClick={handleDelete(item)}>
        Удалить
      </Button>
    </div>
  );

  const moreFormatter = (_, item) => (
    <TooltipWithPortal
      content={<Icons customIcon icon="more-vert" className="more-vert" />}
      classNamePortal="more-actions"
      tooltipContent={renderContextMenu(item)}
      classNameWrapper="more-tooltip-wrapper"
      className="more-tooltip"
    />
  );

  return (
    <div className="page application-page">
      <TimerLoader global loading={loadingReportList || loadingCurrentReport || loadingEditReport || loadingDeleteReport || loadingCreateReport} />
      <div className="page-title">
        <div className="title-text">{formatMessage({ id: 'menu.report.list' })}</div>
        <FilterRow>
          <FieldDTO
            control={control}
            component={RangeDatePicker}
            dtoObject={{
              name: 'created_at_from',
              type: String,
              labelKey: 'created_at',
            }}
            usePortal
            placeholder="Дата"
            changeFormat="YYYY-MM-DD"
            inputProps={{
              value: [getValues('created_at_from'), getValues('created_at_to')],
              onChange: e => {
                const v = e.target.value;
                setValue('created_at_from', v[0]);
                setValue('created_at_to', v[1]);
              },
            }}
            className="no-border"
          />
          <div className="filter-sep" />
          <Button variant="second" className="" onClick={() => { setIsShowForm(true); setIsEdit(false); }}>
            <Icons
              customIcon
              className="file-download"
              width={24}
              height={24}
              icon="plus"
            />
          </Button>
        </FilterRow>
      </div>
      <div className="page-content-wrapper">
        <div className="page-content" ref={blockRef}>
          <div className="grid-wrapper frame">
            <div className="custom-scroll-theme sticky-table-wrapper">
              <CustomizedGrid
                data={reportList}
                columns={columns}
                name="report-list"
                settingsFormatter={moreFormatter}
                renderContextMenu={renderContextMenu}
                changeSort={changeSort}
                sort={sort}
                rowClassFormatter={rowClassFormatter}
                onDoubleClickByRow={item => handleView(item)}
              />
            </div>
          </div>
          <Pagination meta={reportListMeta} list={reportList} handleChangeMeta={loadData} />
        </div>
      </div>
      {isShowForm ? <ReportForm key={currentReport && currentReport.id} isEdit={isEdit} current={currentReport} loadData={loadData} close={handleClose} /> : null}
      {showFile ? <ViewFile file={showFile} handleClose={handleCloseViewFile} title="Просмотр отчета" /> : null}
    </div>
  );
}
